import React, { useState } from 'react'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  TextField,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'

import PropTypes from 'prop-types'

import {
  StyledApproveButton,
  StyledButtonContainer,
  StyledMutedButton,
} from '../ApprovalView.styles'

const REJECTION_REASONS = [
  'Project Related Issues',
  'Hours Related Issues',
  'Comment Related Issue',
  'Other',
]

const ApprovalsActionButtons = ({ entry, pendingActions, setPendingActions }) => {
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const currentAction = pendingActions[entry._id]?.action
  // Map 'reject' to 'send_back' for display, default to 'approve'
  const selectedAction = currentAction === 'reject' ? 'send_back' : currentAction || 'approve'

  const handleRejectDialogClose = () => {
    setIsRejectDialogOpen(false)
  }

  const handleActionChange = event => {
    const action = event.target.value

    if (action === 'send_back') {
      setIsRejectDialogOpen(true)
    } else if (action === 'approve') {
      setPendingActions({
        ...pendingActions,
        [entry._id]: {
          action: 'approve',
        },
      })
    }
  }

  return (
    <StyledButtonContainer>
      <Select
        value={selectedAction}
        onChange={handleActionChange}
        displayEmpty
        style={{ minWidth: '120px' }}
      >
        <MenuItem value="approve">Approve</MenuItem>
        <MenuItem value="send_back">Send Back</MenuItem>
      </Select>

      {currentAction === 'reject' && (
        <Tooltip title="Edit rejection reason">
          <IconButton
            size="small"
            onClick={() => setIsRejectDialogOpen(true)}
            style={{ padding: '4px' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <RejectDialog
        open={isRejectDialogOpen}
        onCancel={handleRejectDialogClose}
        onAccept={reason => {
          setPendingActions({
            ...pendingActions,
            [entry._id]: {
              action: 'reject',
              rejectionReason: reason,
            },
          })
          handleRejectDialogClose()
        }}
        initialReason={pendingActions[entry._id]?.rejectionReason || ''}
      />
    </StyledButtonContainer>
  )
}

const RejectDialog = ({ open, onAccept, onCancel, initialReason }) => {
  const [selectedReason, setSelectedReason] = useState('')
  const [otherText, setOtherText] = useState('')

  React.useEffect(() => {
    if (open) {
      if (REJECTION_REASONS.includes(initialReason)) {
        setSelectedReason(initialReason)
        setOtherText('')
      } else if (initialReason) {
        setSelectedReason('Other')
        setOtherText(initialReason)
      } else {
        setSelectedReason('')
        setOtherText('')
      }
    }
  }, [open, initialReason])

  const handleAccept = () => {
    const finalReason = selectedReason === 'Other' ? otherText : selectedReason
    onAccept(finalReason)
    setSelectedReason('')
    setOtherText('')
  }

  const isConfirmDisabled = !selectedReason || (selectedReason === 'Other' && !otherText.trim())

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Reject Reason</DialogTitle>
      <DialogContent>
        <FormControl variant="outlined" fullWidth style={{ minWidth: 250, marginTop: 8 }}>
          <InputLabel>Reason</InputLabel>
          <Select
            value={selectedReason}
            onChange={e => setSelectedReason(e.target.value)}
            label="Reason"
          >
            {REJECTION_REASONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedReason === 'Other' && (
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Please specify..."
            value={otherText}
            onChange={e => setOtherText(e.target.value)}
            style={{ marginTop: 12 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <StyledMutedButton onClick={onCancel}>Cancel</StyledMutedButton>
        <StyledApproveButton onClick={handleAccept} disabled={isConfirmDisabled}>
          Confirm
        </StyledApproveButton>
      </DialogActions>
    </Dialog>
  )
}

ApprovalsActionButtons.propTypes = {
  entry: PropTypes.object.isRequired,
  pendingActions: PropTypes.object,
  setPendingActions: PropTypes.func.isRequired,
}

RejectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialReason: PropTypes.string,
}

export default ApprovalsActionButtons
